import { Dispatch, SetStateAction } from 'react'
import { isSameDay } from 'date-fns'
import { manualRequestsService } from '../../services/myActionsService'
import { BaseResponse } from '../../types/base-response'
import { dateInYyyyMmDdHhMmSs } from '../../utils/date-utils'
import { AppDispatch } from '../../redux/store'
import UserErrorMessage from '../../utils/errorFilter'
import { showErrorMessage } from '../../redux/reducers/snackbarReducer'
import { AbsenceDrawerValidationErrors, AbsenceRequestModel } from './model'
import { setSelectedDays } from '../../redux/reducers/timeOffRequestsReducer'
import { AbsenceDayRequest } from '../../types/absence'

export interface ShiftData {
  hours: number
  startTime: Date
  desc: string
  success: boolean
}

export interface ShiftGetAbsentHoursResponse extends BaseResponse {
  shiftHours: number | undefined
  formattedShiftString: string | undefined
}

export const calculatePartDayAbsence = async (
  absenceData: AbsenceRequestModel,
  selectedDays: AbsenceDayRequest[] | null,
  dispatch: AppDispatch,
  setData: Dispatch<SetStateAction<AbsenceRequestModel>>,
  leftWorkAt: Date | undefined,
  allSelectedDays?: AbsenceDayRequest[] | null
): Promise<void> => {

  if (!selectedDays || selectedDays.length !== 1) {
    return
  }

  const shiftDate = selectedDays[0].day
  if (!shiftDate) {
    return
  }

  const shiftDateString = dateInYyyyMmDdHhMmSs(
    typeof shiftDate === 'string' ? new Date(shiftDate) : shiftDate
  )

  const absentFromDate =
    typeof absenceData.absentFrom === 'string'
      ? new Date(absenceData.absentFrom)
      : absenceData.absentFrom

  if (!absentFromDate) {
    return
  }

  const absentFromString = dateInYyyyMmDdHhMmSs(absentFromDate)

  manualRequestsService
    .getShiftAbsentHours(
      absenceData.employeeId,
      shiftDateString, 
      absenceData.partDayAbsence,
      absentFromString, 
      absenceData.hasHadLunch,
      absenceData.shiftDescription
    )

    .then(res => {
      if (res.shiftHours !== undefined) {
        const shiftDateObj = typeof shiftDate === 'string' ? new Date(shiftDate) : shiftDate
        
        if (allSelectedDays && allSelectedDays.length > 0) {
          const updatedDays = allSelectedDays.map(day => {
            const dayDate = typeof day.day === 'string' ? new Date(day.day) : day.day
            return isSameDay(dayDate, shiftDateObj)
              ? { ...day, hours: res.shiftHours! }
              : day
          })
          dispatch(setSelectedDays(updatedDays))
        } else {
          const updatedDay = { ...selectedDays[0], hours: res.shiftHours }
          dispatch(setSelectedDays([updatedDay]))
        }
      }
      if (res.formattedShiftString !== undefined) {
        setData(currentAbsenceData => ({
          ...currentAbsenceData,
          shiftDescription: res.formattedShiftString!,
        }))
      }
    })
    .catch(err => {
      const response: BaseResponse = err.response?.data ?? { errors: [] }
      response.errors.forEach(error => {
        dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
      })
    })
}

type ValidationErrorType = keyof AbsenceDrawerValidationErrors
const validationErrorMessageMap: Record<ValidationErrorType, string> = {
  employeeId: 'Employee is invalid',
  absenceTypeId: 'Absence type is invalid',
  absenceTypeReasonId: 'Absence reason is invalid',
  weddingCertificateSubmitted: 'Wedding certificate confirmation is missing',
  bloodDonationConfirmed: 'Blood donation confirmation is missing',
  comments: 'Comments are missing',
  expectedReturn: 'Expected return date is invalid',
  contactDateTime: 'Contact date and time is invalid',
  dateRange: 'Absence date range is invalid',
  absentFromDate: 'Absent from date is invalid'
}

export const getValidationErrorMessages = (errors: AbsenceDrawerValidationErrors): string[] => {
  const errorMessages: string[] = []
  Object.keys(errors).forEach((key) => {
    const errorType = key as ValidationErrorType
    if (errors[errorType]) {
      errorMessages.push(validationErrorMessageMap[errorType])
    }
  })
  return errorMessages
}